import emailjs from "@emailjs/browser";
import { motion, useInView } from "framer-motion";
import React, { FormEvent, useRef, useState } from "react";
import { useContact } from "../contexts/contactContext";
import { InputField, TextAreaField } from "./FormFields";
import { STANDARD_VARIANTS } from "../constants/animations";
import { BUTTON_STYLES, CONTAINER_STYLES, TYPOGRAPHY_STYLES } from "../constants/styles";

const Contact: React.FC = () => {
  const {
    name,
    setName,
    email,
    setEmail,
    message,
    setMessage,
    title,
    setTitle,
  } = useContact();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);

    try {
      await emailjs.send(
        "service_win92lk",
        "template_u6cx73w",
        {
          to_name: "Tomer",
          from_name: name,
          from_email: email,
          subject: title,
          message: message,
        },
        "M_GdCFDEmL-dmTZR2"
      );
      try {
        await emailjs.send(
          "service_win92lk",
          "template_jgcw8ba",
          {
            to_name: "Tomer",
            from_name: name,
            from_email: email,
          },
          "M_GdCFDEmL-dmTZR2"
        );
      } catch {
        // The inquiry itself was delivered; a failed auto-reply is not a user-facing error.
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Failed to send email:", error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };


  const tickVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeInOut" },
    },
  };

  return (
    <div ref={ref} className={CONTAINER_STYLES.full}>
      <motion.h2
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={STANDARD_VARIANTS.item}
        className={`${TYPOGRAPHY_STYLES.sectionTitle} mb-12`}
      >
        Let's Talk
      </motion.h2>
      {!isSubmitted ? (
        <motion.form
          variants={STANDARD_VARIANTS.container}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          onSubmit={handleSubmit}
          className="max-w-md mx-auto space-y-4"
        >
          <motion.div variants={STANDARD_VARIANTS.item}>
            <InputField
              name="name"
              value={name}
              onChange={setName}
              autoComplete="name"
              maxLength={100}
              required
            />
          </motion.div>
          <motion.div variants={STANDARD_VARIANTS.item}>
            <InputField
              name="email"
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              maxLength={254}
              placeholder="you@company.com"
              required
            />
          </motion.div>
          <motion.div variants={STANDARD_VARIANTS.item}>
            <InputField
              name="title"
              label="Subject"
              value={title}
              onChange={setTitle}
              maxLength={150}
              placeholder="What's this about?"
              required
            />
          </motion.div>
          <motion.div variants={STANDARD_VARIANTS.item}>
            <TextAreaField
              name="message"
              value={message}
              onChange={setMessage}
              maxLength={5000}
              placeholder="What are you building, and how can I help?"
              required
            />
          </motion.div>
          <motion.button
            variants={STANDARD_VARIANTS.item}
            type="submit"
            disabled={isSubmitting}
            className={`${BUTTON_STYLES.primaryFull} disabled:opacity-50`}
          >
            {isSubmitting ? "Sending..." : "Send Message"}
          </motion.button>
          {submitError && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              role="alert"
              className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200"
            >
              Your message couldn't be sent, but it's still here. Try again, or
              email me directly at{" "}
              <a
                href="mailto:mail@tomerrosenfeld.com"
                className="font-semibold underline"
              >
                mail@tomerrosenfeld.com
              </a>
              .
            </motion.div>
          )}
        </motion.form>
      ) : (
        <motion.div
          initial="hidden"
          animate="visible"
          className="flex flex-col items-center justify-center space-y-4"
        >
          <motion.svg
            width="100"
            height="100"
            viewBox="0 0 100 100"
            className="text-accent-900 dark:text-surface-50"
          >
            <motion.path
              d="M20,50 L40,70 L80,30"
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              variants={tickVariants}
            />
          </motion.svg>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-2xl font-bold text-center dark:text-surface-50 text-accent-900"
          >
            Talk to you soon!
          </motion.p>
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            onClick={() => {
              setTitle("");
              setMessage("");
              setIsSubmitted(false);
            }}
            className="text-primary-600 dark:text-primary-400 font-semibold hover:underline"
          >
            Send another message
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default Contact;
